import React, { useState, useEffect, useMemo } from "react";
import { OI_URL } from "../../config/config";
import DataFetchHookOI from "../../helper/DataFetchHookOI";
import PermissionsRequired from "../../helper/PermissionsRequired";
import AddBinCaidModal from "./AddBinCaidModal";
import EditBinCaidModal from "./EditBinCaidModal";
import { Table, PageHeader, Button, Card, Form, Select, Row, Col, Input } from "antd";
import { SearchOutlined, FileSearchOutlined, PlusOutlined } from "@ant-design/icons";
import AssociationsChecker from "../../helper/AssociationsChecker";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import SortOrder from "../../helper/Table/SortOrder";
import moment from "moment";
// import Cookies from "js-cookie";
import ImportBinCaidModal from "./ImportBinCaidModal";
import axios from "axios";
import Cookies from "js-cookie";

const { Option } = Select;
const DATA_URL = "/api/v1/txshield/bincaidList";
const MERCHANT_DATA_URL = "/api/v1/txshield/merchants/";

const BinCaidList = (props) => {
    const [{ data, totalResult, isLoading, requestData }, doFetch, setRequestData] = DataFetchHookOI(DATA_URL, "GET", null, {
        page: 1,
        size: 20,
        order: "ASC",
    });
    const [form] = Form.useForm();
    const [currentPage, setCurrentPage] = useState(1);
    const [sortedInfo, setSortedInfo] = useState({});
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [selectedBinCaid, setSelectedBinCaid] = useState(null);
    const [merchantList, setMerchantList] = useState([]);
    const { t } = props;

    // Initialize form with merchant associations included
    const initFormData = useMemo(
        () => ({
            field: "bin",
            keyword: null,
            mcc: null,
            status: null,
            order: "ASC",
        }),
        []
    );

    useEffect(() => {
        // Initialize form values
        form.setFieldsValue(initFormData);
    }, [form, initFormData]);

    useEffect(() => {
        getMerchantList();
    }, []);

    const getMerchantList = async () => {
        try {
            const response = await axios.post(
                OI_URL + MERCHANT_DATA_URL,
                { order: 'ASC', sortField: 'name', page: 1, size: 1000 },
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
            );
            setMerchantList(response.data.datalist || []);
        } catch (error) {
            console.error("Failed to fetch merchants", error);
        }
    };

    const showEditBinCaidModal = (record) => {
        setSelectedBinCaid(record);
        setIsEditModalVisible(true);
    };

    const handleChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
    };

    // Update clearAll to preserve merchant associations for non-SysAdmin users
    const clearAll = () => {
        // Create reset data with proper associations
        const resetFormData = { ...initFormData };

        form.resetFields();
        form.setFieldsValue(resetFormData);

        // Create params object with timestamp and associations
        const params = {
            ...resetFormData,
            page: 1,
            timestamp: moment().unix(),
        };

        setRequestData(params);
        const queryString = new URLSearchParams(params).toString();
        doFetch(`${OI_URL}${DATA_URL}?${queryString}`, "GET");
    };

    const fetchBinCaids = () => {
        doFetch(`${OI_URL}${DATA_URL}?timestamp=${moment().unix()}`);
    };

    // Update onSearch to ensure merchant associations are preserved
    const onSearch = (values) => {
        const searchData = {
            keyword: values.keyword ? values.keyword.trim() : null,
            field: values.field,
            mcc: values.mcc,
            status: values.status,
        };

        if (values.associatedMerchantIds && values.associatedMerchantIds.length > 0) {
            // If user has selected specific merchants, use those
            searchData.associatedMerchantIds = values.associatedMerchantIds;
        }

        // remove any null key
        const filteredSearchData = Object.fromEntries(Object.entries(searchData).filter(([_, v]) => v !== null && v !== "" && v !== undefined));

        setCurrentPage(1);
        const params = { ...filteredSearchData };
        const queryString = new URLSearchParams(params).toString();

        doFetch(`${OI_URL}${DATA_URL}?${queryString}`, "GET");
    };

    const columns = [
        {
            dataIndex: "bin",
            key: "bin",
            title: t("tables.fields.bin"),
            sorter: {
                compare: (a, b) => String(a.bin).localeCompare(String(b.bin)),
            },
            sortOrder: SortOrder(sortedInfo, "bin"),
        },
        {
            dataIndex: "caid",
            key: "caid",
            title: t("tables.fields.caid"),
            sorter: {
                compare: (a, b) => String(a.caid).localeCompare(String(b.caid)),
            },
            sortOrder: SortOrder(sortedInfo, "caid"),
        },
        {
            dataIndex: "mcc",
            key: "mcc",
            title: t("tables.fields.mcc"),
            sorter: {
                compare: (a, b) => String(a.mcc).localeCompare(String(b.mcc)),
            },
            sortOrder: SortOrder(sortedInfo, "mcc"),
        },
        {
            dataIndex: "status",
            key: "status",
            title: t("tables.fields.status"),
            sorter: {
                compare: (a, b) => String(a?.status || "").localeCompare(String(b?.status || "")),
            },
            sortOrder: SortOrder(sortedInfo, "status"),
            render: (text) => String(text).toUpperCase(),
        },
        {
            dataIndex: "merchantId",
            key: "merchantId",
            title: t("tables.fields.merchant"),
            sorter: (a, b) => {
                const nameA = merchantList.find(m => m.merchantId === a.merchantId)?.name || "";
                const nameB = merchantList.find(m => m.merchantId === b.merchantId)?.name || "";
                return nameA.localeCompare(nameB);
            },
            sortOrder: SortOrder(sortedInfo, "merchantId"),
            render: (_, record) => (
                <span>{merchantList.find(m => m.merchantId === record.merchantId)?.name || ""}</span>
            ),
        },
        {
            dataIndex: "referenceNumber",
            key: "referenceNumber",
            title: t("tables.fields.reference_number"),
            sorter: {
                compare: (a, b) => String(a?.referenceNumber || "").localeCompare(String(b?.referenceNumber || "")),
            },
            sortOrder: SortOrder(sortedInfo, "referenceNumber"),
        },
        {
            dataIndex: "action",
            title: t("tables.fields.action"),
            align: "left",
            fixed: "right",
            width: 120,
            render: (text, record) => (
                <Button type="link" onClick={() => showEditBinCaidModal(record)} style={{ marginLeft: "auto" }}>
                    <FileSearchOutlined style={{ fontSize: "18px", color: "rgba(0, 0, 0, 0.85)" }} />
                </Button>
            ),
        },
    ];

    const getDataSource = (datalist) => {
        return datalist && Array.isArray(datalist) ? datalist : [];
    };

    const getHeaderTable = () => (
        <div className="custom-table--header d-flex justify-content-between align-items-center">
            <h3 className="table-title">{t("contents.oi.bincaids")}</h3>

            {PermissionsRequired("SYSADMIN") && (
                <Button type="primary" onClick={() => setIsAddModalVisible(true)} style={{ marginLeft: "auto", position: "relative", zIndex: 10 }}>
                    <PlusOutlined /> {t("contents.oi.new_bincaid")}
                </Button>
            )}
        </div>
    );

    const AdvancedSearchForm = () => (
        <div className="search-wrap">
            <Card title={t("tables.actions.search")} size="small">
                <Form form={form} size="large" name="advanced_search" className="ant-advanced-search-form" onFinish={onSearch} initialValues={{ field: "bin" }}>
                    <Row gutter={[16, 16]}>
                        <Col className="gutter-row" xs={24} sm={12} md={6} lg={6} xl={4}>
                            <Form.Item name="field">
                                <Select showSearch style={{ width: "100%" }} placeholder={t("tables.fields.field")}>
                                    <Option value="bin">{t("tables.fields.bin")}</Option>
                                    <Option value="caid">{t("tables.fields.caid")}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={24} sm={12} md={6} lg={6} xl={4}>
                            <Form.Item name="keyword">
                                <Input placeholder={t("tables.fields.keyword")} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={24} sm={12} md={6} lg={6} xl={4}>
                            <Form.Item name="mcc">
                                <Input placeholder={t("tables.fields.mcc")} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={24} sm={12} md={6} lg={6} xl={4}>
                            <Form.Item name="status">
                                <Select showSearch style={{ width: "100%" }} placeholder={t("tables.fields.status")}>
                                    <Option value="requested">REQUESTED</Option>
                                    <Option value="pending">PENDING</Option>
                                    <Option value="enrolled">ENROLLED</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={24} sm={12} md={6} lg={6} xl={4}>
                            <Form.Item>
                                <Button type="secondary" onClick={clearAll} block>
                                    {t("tables.actions.clear_all")}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={24} sm={12} md={6} lg={6} xl={4}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    <SearchOutlined /> {t("tables.actions.search")}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );

    if (!(PermissionsRequired("SYSADMIN") || AssociationsChecker("OI") )) {
        return null;
    }

    return (
        <div>
            <PageHeader title={t("contents.oi.bincaids")}></PageHeader>
            <AdvancedSearchForm />

            {PermissionsRequired("SYSADMIN") && (
                <ImportBinCaidModal
                    t={t}
                    keyname="import_csv"
                    title={t("tables.actions.importbinmid")}
                    style={{ marginLeft: 8 }}
                    setIsImportBinMidModalVisible={setIsImportModalVisible}
                    isImportBinMidModalVisible={isImportModalVisible}
                    fetchBinCaids={fetchBinCaids}
                    merchantList={merchantList}
                />
            )}
            {PermissionsRequired("SYSADMIN") && (
                <AddBinCaidModal
                    t={t}
                    isAddBinCaidModalVisible={isAddModalVisible}
                    setIsAddBinCaidModalVisible={setIsAddModalVisible}
                    fetchBinCaids={fetchBinCaids}
                    descriptorId={null} // Explicitly pass null for non-descriptor context
                    merchantList={merchantList}
                />
            )}
            {PermissionsRequired("SYSADMIN") && (
                <EditBinCaidModal
                    t={t}
                    binCaid={selectedBinCaid}
                    isEditModalVisible={isEditModalVisible}
                    setIsEditModalVisible={setIsEditModalVisible}
                    fetchBinCaids={fetchBinCaids}
                    merchantList={merchantList}
                />
            )}
            <Table
                loading={isLoading}
                columns={columns}
                title={() => getHeaderTable()}
                rowKey="bincaidId"
                className="table-v3 custom-pagination"
                size="small"
                dataSource={getDataSource(data.datalist)}
                onChange={handleChange}
                pagination={CustomPaginationV2({ totalResult, currentPage, requestData, setRequestData, setCurrentPage })}
                locale={{ emptyText: "No records found" }}
            />
        </div>
    );
};

export default BinCaidList;
